import {
  List,
  Datagrid,
  FunctionField,
  TextInput,
  BooleanInput,
  SelectInput,
  RaRecord,
} from 'react-admin';
import { AvailabilityCell, CompanyCell, PayloadCell } from './cells';
import { VehicleInlineCell, DriverInlineCell, formatVehicleYear } from './listCells';
import { ChipCell, NoWrap, compactDatagridSx } from '../../shared/listCells';
import { Chip, Stack, Tooltip } from '@mui/material';
import { SyncStatus } from '../SyncStatus';
import { menuLabel } from '../menuConfig';
import { ListExportActions } from '../../shared/ListExportActions';
import { fleetExporter, fleetExporterLegacy } from './exportColumns';
import { FleetListOnlyMine } from './FleetListOnlyMine';
import { vehicleTypeLabel } from './vehicleTypes';
import { INCLUDE_ARCHIVED_FILTER, INCLUDE_ARCHIVED_LABEL } from './archivedVehicles';
import { useFeature } from '../featureFlags/FeatureGate';
import { FLEET_CORRECTIONS, FLEET_HIDE_ARCHIVED, OPERATIONAL_LABEL } from './fleetFeatures';

/**
 * Typy pojazdów z kartoteki klienta.
 *
 * Wartości pochodzą z ich TMS-u — nie tłumaczymy ich, bo planista zna je
 * z systemu, którego używa na co dzień, a tłumaczenie rozjechałoby się przy
 * pierwszym nowym typie. Wyjątek to zbiorcze „OTHER” (`vehicleTypeLabel`);
 * wartość filtra (`id`) zostaje.
 */
const CAR_TYPES = ['JUMBO TRUCK', 'TRAILER', 'FORKLIFT', 'MEGA TRUCK', 'SOLO', 'OTHER'].map((id) => ({
  id,
  name: vehicleTypeLabel(id),
}));

/**
 * Filtry listy floty według przełączników konta (12.10.2026): „Pokaż także
 * archiwalne” tylko z `fleetHideArchived`, podpis „W eksploatacji” tylko
 * z `operationalLabel` — wyłączone dają filtry jak na produkcji 0.31.3.
 */
export const carFilters = ({ hideArchived, operationalLabel }: { hideArchived: boolean; operationalLabel: boolean }) => [
  <TextInput source="q" label="Szukaj (numer / model)" alwaysOn />,
  // Poprawa brzmienia 01.10.2026: etykieta mówi, co filtr ukrywa (wcześniej
  // „Tylko pod zlecenie”). Klucz `ciagniki` bez zmian.
  <BooleanInput source="ciagniki" label="Bez naczep i wózków" alwaysOn />,
  // Domyślnie wyłączony = API pomija archiwalne (07.10.2026, `archivedVehicles.tsx`).
  ...(hideArchived
    ? [<BooleanInput source={INCLUDE_ARCHIVED_FILTER} label={INCLUDE_ARCHIVED_LABEL} alwaysOn />]
    : []),
  <SelectInput source="carType" label="Typ" choices={CAR_TYPES} />,
  // `defaultValue`: filtr dodany z „Dodaj filtr” od razu działa — bez tego
  // pojawiał się wyłączony i trzeba było go przełączyć (e2e, 28.09.2026).
  // Krótsze etykiety: przy 1920 px komplet filtrów nie mieścił się w jednej
  // linii i „z kierowcą” spadało niżej. Klucze (`source`) bez zmian.
  <BooleanInput source="available" label="Dostępne" defaultValue />,
  // „W eksploatacji”, nie „Sprawne” (08.10.2026, decyzja użytkownika):
  // `operational` to `vehicle_operational` z TMS, którego fałsz ekran
  // podpisuje „poza eksploatacją” (`cars/filters.ts`) — o usterce nie mówi.
  <BooleanInput source="operational" label={operationalLabel ? 'W eksploatacji' : 'Sprawne'} defaultValue />,
  <BooleanInput source="bezSpolki" label="Bez spółki" defaultValue />,
  <BooleanInput source="zKierowca" label="Z kierowcą" defaultValue />,
];

/**
 * Domyślnie „Bez naczep i wózków” (28.09.2026). Połowa kartoteki to naczepy
 * i wózki, a naczepy mają własną pozycję w menu — lista „Flota” startująca
 * od nich wyglądała na pomyłkę. Klucz `ciagniki` zostaje: to dana, którą
 * react-admin zapamiętuje, a pulpit buduje nim linki.
 */
export const FLEET_FILTER_DEFAULTS = { ciagniki: true } as const;

/**
 * Własny klucz zapamiętanych parametrów listy. Domyślny (`cars.listParams`)
 * dzieliła z „Flotą” lista naczep (ten sam zasób `cars`), a react-admin
 * stosuje wartości domyślne filtra tylko wtedy, gdy nic nie zapamiętał —
 * kto raz otworzył którąkolwiek z tych list, nie zobaczyłby nowego
 * domyślnego filtra nigdy. Zmiana klucza raz zapomina zapamiętany stan
 * listy floty (sortowanie, filtry); same klucze filtrów bez zmian.
 */
export const FLEET_LIST_STORE_KEY = 'fleet.listParams';

/**
 * Flota — lista.
 *
 * Kartoteka pochodzi z TMS klienta i jest **tylko do odczytu**: to on jest jej
 * właścicielem, a edycja u nas rozjechałaby się przy najbliższym imporcie.
 *
 * Startuje od pojazdów pod zlecenie (`FLEET_FILTER_DEFAULTS`); całą
 * kartotekę, z naczepami i wózkami, pokazuje wyłączenie przełącznika.
 * Archiwalnych (poza eksploatacją wg TMS) API nie oddaje bez „Pokaż także
 * archiwalne” — domyślna wartość nie siedzi w `FLEET_FILTER_DEFAULTS`, bo
 * ukrywanie ma działać także przy zapamiętanych filtrach i linkach z pulpitu.
 */
export const CarList = () => (
  <CarListView
    hideArchived={useFeature(FLEET_HIDE_ARCHIVED)}
    operationalLabel={useFeature(OPERATIONAL_LABEL)}
    corrections={useFeature(FLEET_CORRECTIONS)}
  />
);

/**
 * Znacznik „≠ TMS” przy aucie z korektą kartoteki (22.10.2026,
 * `fleetCorrections`; makieta FTL: lista pojazdów) — API oddaje liczbę
 * skorygowanych pól (`corrections`).
 */
export const CorrectionListMarker = ({ record }: { record: RaRecord }) => {
  const n = Number(record.corrections ?? 0);
  if (!(n > 0)) return null;
  return (
    <Tooltip title="Kartoteka zawiera wartości skorygowane w aplikacji, różne od TMS — szczegóły na karcie pojazdu.">
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label="≠ TMS"
        data-testid="fleet-correction-marker"
        sx={{ ml: 0.5 }}
      />
    </Tooltip>
  );
};

const CarListView = ({
  hideArchived,
  operationalLabel,
  corrections = false,
}: {
  hideArchived: boolean;
  operationalLabel: boolean;
  corrections?: boolean;
}) => (
  <List
    filters={carFilters({ hideArchived, operationalLabel })}
    filterDefaultValues={FLEET_FILTER_DEFAULTS}
    storeKey={FLEET_LIST_STORE_KEY}
    // Eksport całości z polskimi nagłówkami (28.09.2026: plik miał 200 z 608).
    exporter={operationalLabel ? fleetExporter : fleetExporterLegacy}
    actions={<ListExportActions />}
    sort={{ field: 'registrationNumber', order: 'ASC' }}
    perPage={25}
    title={menuLabel('cars')}
  >
    <Stack spacing={1}>
      {/* Kartoteka pochodzi z TMS (W12): kiedy ostatnio pobrana i z jakim wynikiem. */}
      <SyncStatus />
      {/* Opiekun FTL pracuje z listy aut (status 29.09.2026) — filtr, nie uprawnienie. */}
      <FleetListOnlyMine />
    <Datagrid rowClick="show" bulkActionButtons={false} sx={compactDatagridSx}>
      {/* Numer z modelem w jednej linii — wiersz ma jedną wysokość, nie dwie. */}
      <FunctionField label="Pojazd" render={(r: RaRecord) => <VehicleInlineCell record={r} />} />
      {/* `source` zostaje: po nim się sortuje; na ekranie etykieta typu. */}
      <FunctionField
        source="carType"
        label="Typ"
        sx={{ whiteSpace: 'nowrap' }}
        render={(r: RaRecord) => vehicleTypeLabel(r.carType)}
      />
      <FunctionField
        label="Dostępność"
        render={(r: RaRecord) => (
          <ChipCell max={220}>
            <AvailabilityCell record={r} />
          </ChipCell>
        )}
      />
      <FunctionField
        label="Ładowność"
        render={(r: RaRecord) => (
          <NoWrap>
            <PayloadCell record={r} />
          </NoWrap>
        )}
      />
      <FunctionField
        label="Kierowca"
        render={(r: RaRecord) =>
          corrections ? (
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              <DriverInlineCell record={r} />
              <CorrectionListMarker record={r} />
            </Stack>
          ) : (
            <DriverInlineCell record={r} />
          )
        }
      />
      {/* `source` zostaje: po nim się sortuje. Rok 0 z TMS to myślnik. */}
      <FunctionField
        source="vehicleYear"
        label="Rocznik"
        render={(r: RaRecord) => formatVehicleYear(r.vehicleYear)}
      />
      <FunctionField label="Spółka" render={(r: RaRecord) => <CompanyCell record={r} />} />
    </Datagrid>
    </Stack>
  </List>
);
